import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Award, Plus, X, CheckCircle2, XCircle, Clock, Ban, Filter } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const KOMPETENSOMRADEN = [
  'Kommunikation', 'Teamarbete', 'Problemlösning', 'Tidsplanering',
  'Ledarskap', 'Anpassningsförmåga', 'Teknisk kompetens', 'Kundservice',
  'Initiativförmåga', 'Kvalitetsmedvetenhet', 'Självständighet',
  'Analytisk förmåga', 'Kreativitet', 'Flexibilitet', 'Stresshantering',
];

const STATUS_CONFIG = {
  pagaende:  { label: 'Pågående',  className: 'bg-blue-100 text-blue-700',   icon: Clock        },
  godkand:   { label: 'Godkänd',   className: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  underkand: { label: 'Underkänd', className: 'bg-red-100 text-red-700',     icon: XCircle      },
  avbruten:  { label: 'Avbruten',  className: 'bg-gray-100 text-gray-500',   icon: Ban          },
};

const NIVA_CONFIG = {
  bas:       { label: 'Bas',       className: 'bg-gray-100 text-gray-600'    },
  mellan:    { label: 'Mellan',    className: 'bg-primary/20 text-[#333]'    },
  avancerad: { label: 'Avancerad', className: 'bg-gray-700 text-white'       },
};

const selectCls = 'h-9 w-full px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, className: 'bg-muted text-muted-foreground' };
  return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${cfg.className}`}>{cfg.label}</span>;
}

function NivaBadge({ niva }) {
  const cfg = NIVA_CONFIG[niva] || { label: niva, className: 'bg-muted text-muted-foreground' };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.className}`}>{cfg.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Main page ──────────────────────────────────────────────────────────────────

const OCNValidationPage = () => {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'super_admin' || user?.role === 'customer_admin';
  const canWrite = user?.role !== 'read_only_assigner' && user?.role !== 'read_only_participant';

  const [validations, setValidations] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNiva, setFilterNiva]     = useState('');

  // Inline create
  const [createOpen, setCreateOpen] = useState(false);
  const EMPTY_CREATE = {
    participant_id: '', bedomare: user?.name || '',
    kompetensomrade: KOMPETENSOMRADEN[0], niva: 'bas',
    valideringsmalsattning: '', startdatum: new Date().toISOString().split('T')[0],
  };
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Escape closes forms
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (createOpen) { setCreateOpen(false); setCreateForm(EMPTY_CREATE); setCreateErrors({}); }
      if (editingId)  setEditingId(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [createOpen, editingId]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterNiva)   params.set('niva',   filterNiva);
      const [vRes, pRes] = await Promise.all([
        api.get(`/ocn-validations${params.toString() ? '?' + params : ''}`),
        api.get('/participants'),
      ]);
      setValidations(vRes.data);
      setParticipants(pRes.data);
    } catch {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterNiva]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const participantName = (pid) => participants.find(p => p.id === pid)?.name || '—';

  const now        = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const kpis = {
    pagaende:      validations.filter(v => v.status === 'pagaende').length,
    godkandaManed: validations.filter(v => v.status === 'godkand' && v.updated_at && new Date(v.updated_at) >= monthStart).length,
    snittPoang: (() => {
      const med = validations.filter(v => v.poang != null);
      if (!med.length) return null;
      return Math.round(med.reduce((s, v) => s + v.poang, 0) / med.length);
    })(),
    totalt: validations.length,
  };

  // ── Create ─────────────────────────────────────────────────────────────────

  const handleCreate = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!createForm.participant_id)              errs.participant_id        = 'Välj deltagare';
    if (!createForm.bedomare.trim())             errs.bedomare              = 'Bedömare krävs';
    if (!createForm.valideringsmalsattning.trim()) errs.valideringsmalsattning = 'Målsättning krävs';
    if (!createForm.startdatum)                  errs.startdatum            = 'Startdatum krävs';
    if (Object.keys(errs).length) { setCreateErrors(errs); return; }

    setCreating(true);
    try {
      const res = await api.post('/ocn-validations', {
        ...createForm,
        startdatum: new Date(createForm.startdatum).toISOString(),
      });
      setValidations(prev => [res.data, ...prev]);
      toast.success('Validering skapad');
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      setCreateErrors({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa validering');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────

  const openEdit = (v) => {
    setEditingId(v.id);
    setEditForm({
      status:    v.status    || 'pagaende',
      niva:      v.niva      || 'bas',
      poang:     v.poang     ?? '',
      kommentar: v.kommentar || '',
      slutdatum: v.slutdatum ? v.slutdatum.split('T')[0] : '',
      bedomare:  v.bedomare  || '',
    });
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        status:    editForm.status,
        niva:      editForm.niva,
        bedomare:  editForm.bedomare  || undefined,
        poang:     editForm.poang !== '' ? Number(editForm.poang) : undefined,
        kommentar: editForm.kommentar || undefined,
        slutdatum: editForm.slutdatum ? new Date(editForm.slutdatum).toISOString() : undefined,
      };
      const res = await api.put(`/ocn-validations/${editingId}`, payload);
      setValidations(prev => prev.map(v => v.id === editingId ? res.data : v));
      toast.success('Validering uppdaterad');
      setEditingId(null);
    } catch {
      toast.error('Kunde inte spara ändringar');
    } finally {
      setEditSaving(false);
    }
  };

  const COLS = canWrite ? 8 : 7;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ocn-validation-page">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">OCN Validering</h1>
            <p className="text-muted-foreground mt-1">Kompetensvalidering per deltagare</p>
          </div>
          {canWrite && (
            <Button
              data-testid="create-ocn-btn"
              className="rounded-full font-bold"
              onClick={() => setCreateOpen(v => !v)}
            >
              {createOpen ? <X size={18} className="mr-2" /> : <Plus size={18} className="mr-2" />}
              {createOpen ? 'Stäng' : 'Ny validering'}
            </Button>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Totalt',                   value: kpis.totalt,       color: 'text-foreground'  },
            { label: 'Pågående',                  value: kpis.pagaende,     color: 'text-blue-600'    },
            { label: 'Godkända denna månad',      value: kpis.godkandaManed, color: 'text-green-600'  },
            { label: 'Genomsnittlig poäng',        value: kpis.snittPoang != null ? `${kpis.snittPoang} p` : '—', color: 'text-foreground' },
          ].map(k => (
            <Card key={k.label}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{k.label}</p>
                <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Inline create form */}
        {createOpen && (
          <Card className="border-primary/30" style={{ background: 'rgba(249,207,63,0.05)' }} data-testid="ocn-inline-form">
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4">Ny OCN-validering</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="ocn-participant">Deltagare *</Label>
                    <select
                      id="ocn-participant"
                      data-testid="ocn-participant-select"
                      value={createForm.participant_id}
                      onChange={e => {
                        setCreateForm(f => ({ ...f, participant_id: e.target.value }));
                        if (createErrors.participant_id) setCreateErrors(er => ({ ...er, participant_id: '' }));
                      }}
                      className={`${selectCls}${createErrors.participant_id ? ' border-destructive' : ''}`}
                    >
                      <option value="">Välj deltagare</option>
                      {participants.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {createErrors.participant_id && (
                      <p className="text-xs text-destructive" data-testid="ocn-participant-error">{createErrors.participant_id}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="ocn-kompetens">Kompetensområde *</Label>
                    <select
                      id="ocn-kompetens"
                      data-testid="ocn-kompetens-select"
                      value={createForm.kompetensomrade}
                      onChange={e => setCreateForm(f => ({ ...f, kompetensomrade: e.target.value }))}
                      className={selectCls}
                    >
                      {KOMPETENSOMRADEN.map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="ocn-niva">Nivå *</Label>
                    <select
                      id="ocn-niva"
                      data-testid="ocn-niva-select"
                      value={createForm.niva}
                      onChange={e => setCreateForm(f => ({ ...f, niva: e.target.value }))}
                      className={selectCls}
                    >
                      {Object.entries(NIVA_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="ocn-bedomare">Bedömare *</Label>
                    <Input
                      id="ocn-bedomare"
                      data-testid="ocn-bedomare-input"
                      value={createForm.bedomare}
                      onChange={e => {
                        setCreateForm(f => ({ ...f, bedomare: e.target.value }));
                        if (createErrors.bedomare) setCreateErrors(er => ({ ...er, bedomare: '' }));
                      }}
                      className={`h-9${createErrors.bedomare ? ' border-destructive' : ''}`}
                    />
                    {createErrors.bedomare && (
                      <p className="text-xs text-destructive" data-testid="ocn-bedomare-error">{createErrors.bedomare}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ocn-malsattning">Valideringsmålsättning *</Label>
                  <Textarea
                    id="ocn-malsattning"
                    data-testid="ocn-malsattning-textarea"
                    rows={3}
                    value={createForm.valideringsmalsattning}
                    onChange={e => {
                      setCreateForm(f => ({ ...f, valideringsmalsattning: e.target.value }));
                      if (createErrors.valideringsmalsattning) setCreateErrors(er => ({ ...er, valideringsmalsattning: '' }));
                    }}
                    placeholder="Beskriv målsättningen..."
                    className={createErrors.valideringsmalsattning ? 'border-destructive' : ''}
                  />
                  {createErrors.valideringsmalsattning && (
                    <p className="text-xs text-destructive" data-testid="ocn-malsattning-error">{createErrors.valideringsmalsattning}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ocn-startdatum">Startdatum *</Label>
                  <Input
                    id="ocn-startdatum"
                    data-testid="ocn-startdatum-input"
                    type="date"
                    value={createForm.startdatum}
                    onChange={e => setCreateForm(f => ({ ...f, startdatum: e.target.value }))}
                    className="h-9"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    data-testid="ocn-submit-btn"
                    disabled={creating}
                    className="rounded-full font-bold"
                  >
                    {creating ? 'Skapar...' : 'Skapa validering'}
                  </Button>
                  <Button
                    type="button"
                    data-testid="ocn-cancel-btn"
                    variant="outline"
                    onClick={() => { setCreateOpen(false); setCreateForm(EMPTY_CREATE); setCreateErrors({}); }}
                    className="rounded-full"
                  >
                    Avbryt
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <Filter size={16} className="text-muted-foreground" />
          <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 h-9 text-sm">
              <SelectValue placeholder="Alla statusar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla statusar</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterNiva || 'all'} onValueChange={v => setFilterNiva(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 h-9 text-sm">
              <SelectValue placeholder="Alla nivåer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla nivåer</SelectItem>
              {Object.entries(NIVA_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterStatus || filterNiva) && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setFilterStatus(''); setFilterNiva(''); }}>
              Rensa filter
            </Button>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-12">Laddar...</p>
            ) : validations.length === 0 ? (
              <div className="text-center py-16">
                <Award size={48} className="mx-auto mb-4 text-muted-foreground/20" />
                <p className="text-muted-foreground">
                  Inga valideringar{filterStatus || filterNiva ? ' matchar filtret' : ' ännu'}.
                </p>
                {canWrite && !filterStatus && !filterNiva && (
                  <Button className="mt-4 rounded-full font-bold" onClick={() => setCreateOpen(true)}>
                    <Plus size={16} className="mr-2" /> Skapa första valideringen
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                      <th className="text-left px-4 py-3 font-semibold">Deltagare</th>
                      <th className="text-left px-4 py-3 font-semibold">Kompetensområde</th>
                      <th className="text-left px-4 py-3 font-semibold">Nivå</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-left px-4 py-3 font-semibold">Poäng</th>
                      <th className="text-left px-4 py-3 font-semibold">Startdatum</th>
                      <th className="text-left px-4 py-3 font-semibold">Bedömare</th>
                      {canWrite && <th className="px-4 py-3" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {validations.map(v => (
                      editingId === v.id ? (
                        /* ── Inline edit row ── */
                        <tr key={v.id} data-testid={`ocn-edit-row-${v.id}`} style={{ background: 'rgba(249,207,63,0.05)' }}>
                          <td colSpan={COLS} className="px-4 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Status</Label>
                                <select
                                  data-testid={`ocn-edit-status-${v.id}`}
                                  value={editForm.status}
                                  onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                                  className={selectCls}
                                >
                                  {Object.entries(STATUS_CONFIG).map(([k, c]) => (
                                    <option key={k} value={k}>{c.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Nivå</Label>
                                <select
                                  data-testid={`ocn-edit-niva-${v.id}`}
                                  value={editForm.niva}
                                  onChange={e => setEditForm(f => ({ ...f, niva: e.target.value }))}
                                  className={selectCls}
                                >
                                  {Object.entries(NIVA_CONFIG).map(([k, c]) => (
                                    <option key={k} value={k}>{c.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Poäng</Label>
                                <Input
                                  data-testid={`ocn-edit-poang-${v.id}`}
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={editForm.poang}
                                  onChange={e => setEditForm(f => ({ ...f, poang: e.target.value }))}
                                  className="h-9"
                                  placeholder="0–100"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Slutdatum</Label>
                                <Input
                                  data-testid={`ocn-edit-slutdatum-${v.id}`}
                                  type="date"
                                  value={editForm.slutdatum}
                                  onChange={e => setEditForm(f => ({ ...f, slutdatum: e.target.value }))}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Bedömare</Label>
                                <Input
                                  data-testid={`ocn-edit-bedomare-${v.id}`}
                                  value={editForm.bedomare}
                                  onChange={e => setEditForm(f => ({ ...f, bedomare: e.target.value }))}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Kommentar</Label>
                                <Input
                                  data-testid={`ocn-edit-kommentar-${v.id}`}
                                  value={editForm.kommentar}
                                  onChange={e => setEditForm(f => ({ ...f, kommentar: e.target.value }))}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                data-testid={`ocn-edit-save-${v.id}`}
                                onClick={handleSaveEdit}
                                disabled={editSaving}
                                className="rounded-full font-bold text-xs"
                              >
                                {editSaving ? 'Sparar...' : 'Spara'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`ocn-edit-cancel-${v.id}`}
                                onClick={() => setEditingId(null)}
                                className="rounded-full text-xs"
                              >
                                Avbryt
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        /* ── Normal row ── */
                        <tr key={v.id} data-testid={`ocn-row-${v.id}`} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium">{participantName(v.participant_id)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{v.kompetensomrade}</td>
                          <td className="px-4 py-3"><NivaBadge niva={v.niva} /></td>
                          <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                          <td className="px-4 py-3">{v.poang != null ? `${v.poang} p` : '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{fmtDate(v.startdatum)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{v.bedomare}</td>
                          {canWrite && (
                            <td className="px-4 py-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`edit-ocn-btn-${v.id}`}
                                className="text-xs"
                                onClick={() => openEdit(v)}
                              >
                                Redigera
                              </Button>
                            </td>
                          )}
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default OCNValidationPage;
